/* Copyright (c) 2022 chilled.dev */

import DashboardIcon from '@mui/icons-material/Dashboard';
import FlipIcon from '@mui/icons-material/Flip';
import InfoIcon from '@mui/icons-material/Info';
import {
  AppBar,
  Avatar,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import ErrorBoundary from 'components/error-boundary';
import { FirebaseAuthProvider, useFirebaseAuth } from 'providers/firebase/auth';
import { FirebaseProvider } from 'providers/firebase/firebase';
import { lazy, Suspense, useMemo } from 'react';
import { BrowserRouter as Router, Link, Route, Routes } from 'react-router-dom';
import './App.css';
import Loader from './components/loader';

const Home = lazy(() => import('routes/home'));
const Create = lazy(() => import('routes/create'));
const Scan = lazy(() => import('routes/scan'));
const About = lazy(() => import('routes/about'));

const routes = [
  { path: '/', element: <Home /> },
  { path: '/scan', element: <Scan /> },
  { path: '/about', element: <About /> },
  { path: '/create/:barcode', element: <Create /> },
];

const Auth = () => {
  const user = useFirebaseAuth();
  const googleInfo = useMemo(
    () =>
      user?.providerData.find(({ providerId }) => providerId === 'google.com'),
    [user?.providerData],
  );

  return (
    <Box sx={{ my: 0 }}>
      <Stack direction='row' spacing={2}>
        <Avatar alt='Avatar' src={googleInfo?.photoURL ?? ''} />
        <Typography sx={{ pt: 1 }}>{googleInfo?.displayName}</Typography>
      </Stack>
    </Box>
  );
};

const drawerWidth = 240;

const Nav = () => (
  <Drawer
    variant='permanent'
    sx={{
      'width': drawerWidth,
      'flexShrink': 0,
      '& .MuiDrawer-paper': {
        width: drawerWidth,
        boxSizing: 'border-box',
      },
    }}
    anchor='left'
  >
    <Toolbar />
    <Box sx={{ overflow: 'auto' }}>
      <List
        sx={{
          '& a': {
            textDecoration: 'none',
            color: 'inherit',
          },
        }}
      >
        <Link to='/'>
          <ListItem disablePadding>
            <ListItemButton>
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary='Home' />
            </ListItemButton>
          </ListItem>
        </Link>
        <Link to='/scan'>
          <ListItem disablePadding>
            <ListItemButton>
              <ListItemIcon>
                <FlipIcon />
              </ListItemIcon>
              <ListItemText primary='Scan' />
            </ListItemButton>
          </ListItem>
        </Link>
        <Link to='/about'>
          <ListItem disablePadding>
            <ListItemButton>
              <ListItemIcon>
                <InfoIcon />
              </ListItemIcon>
              <ListItemText primary='About' />
            </ListItemButton>
          </ListItem>
        </Link>
      </List>
    </Box>
  </Drawer>
);

function App() {
  return (
    <Router>
      <ErrorBoundary fallback={<p>Could not initialize app.</p>}>
        <Suspense fallback={<Loader />}>
          <FirebaseProvider>
            <FirebaseAuthProvider>
              <AppBar
                position='fixed'
                sx={{
                  zIndex: (theme) => theme.zIndex.drawer + 1,
                  background: 'background.default',
                }}
                enableColorOnDark
              >
                <Toolbar>
                  <Typography
                    variant='h6'
                    noWrap
                    component='a'
                    href='/'
                    sx={{
                      mr: 2,
                      display: { xs: 'none', md: 'flex' },
                      fontFamily: `'Carter One', cursive`,
                      fontWeight: 400,
                      fontSize: '2.5rem',
                      // letterSpacing: '.3rem',
                      color: 'inherit',
                      textDecoration: 'none',
                    }}
                  >
                    Hive
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Auth />
                </Toolbar>
              </AppBar>
              <Nav />
              <Box component='main' sx={{ flexGrow: 1, p: 2 }}>
                <Toolbar />
                <Routes>
                  {routes.map(({ path, element }) => (
                    <Route key={path} path={path} element={element} />
                  ))}
                </Routes>
              </Box>
            </FirebaseAuthProvider>
          </FirebaseProvider>
        </Suspense>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
